import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Flex,
  Form,
  FormFileInput,
  Link,
  MIME_TYPES,
  Text,
  UploadFileOutlinedIcon,
} from '@shared/components';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { type ErrorDetail } from '../../containers';
import { ImportExamineesViaFileErrorAlert } from '../import-examinees-via-file-error-alert';

const ACCEPTED_FILE_TYPES: string[] = [MIME_TYPES.csv, MIME_TYPES.xls, MIME_TYPES.xlsx];
const MAX_UPLOAD_SIZE = 1024 * 1024 * 20; // 20MB

const ImportExamineesViaFileFormSchema = z.object({
  file: z
    .instanceof(File, {
      message: 'File is required',
    })
    .refine((file) => {
      return file.size <= MAX_UPLOAD_SIZE;
    }, 'ขนาดไฟล์เกิน 20 MB ไม่สามารถอัปโหลดได้')
    .refine((file) => {
      return ACCEPTED_FILE_TYPES.includes(file.type);
    }, 'กรุณาอัปโหลดไฟล์ .CSV หรือ .XLSX เท่านั้น'),
});
export type ImportExamineesViaFileFormInput = z.infer<typeof ImportExamineesViaFileFormSchema>;

interface ImportExamineesViaFileFormProps {
  errors?: Map<string, ErrorDetail[]> | null;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (data: ImportExamineesViaFileFormInput) => void;
  importTemplateUrl: string;
}

export default function ImportExamineesViaFileForm(
  props: Readonly<ImportExamineesViaFileFormProps>,
) {
  const { errors, isLoading, onClose, onSubmit, importTemplateUrl } = props;
  const formContext = useForm<ImportExamineesViaFileFormInput>({
    defaultValues: {
      file: undefined,
    },
    resolver: zodResolver(ImportExamineesViaFileFormSchema),
  });

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>
        <DialogContent>
          <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
            {errors ? <ImportExamineesViaFileErrorAlert errors={errors} /> : null}
            <Text variant='body2Semibold'>
              คุณสามารถอัปโหลดข้อมูลได้สูงสุด 1 ไฟล์ (ขนาดไม่เกิน 20 MB)
            </Text>
            <Box>
              <FormFileInput
                name='file'
                placeholder='กรุณาอัปโหลดไฟล์ CSV หรือ XLSX'
                required
                label='แนบไฟล์'
                accept={ACCEPTED_FILE_TYPES}
              />
              <Text variant='caption' color='text.secondary'>
                ตัวอย่างเอกสารสำหรับอัปโหลด{' '}
                <Link
                  color='info'
                  rel='noreferrer'
                  sx={{ cursor: 'pointer' }}
                  target='_blank'
                  href={importTemplateUrl}
                >
                  ดาวน์โหลดไฟล์ตัวอย่าง
                </Link>
              </Text>
            </Box>
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            color='secondary'
            variant='outlined'
            size='large'
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            size='large'
            loading={isLoading}
            startIcon={<UploadFileOutlinedIcon fontSize='small' />}
          >
            Import
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
